import { useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { BadgeCheck, ArrowDownRight } from "lucide-react";
import { useEffect } from "react";
import { HERO_LOGROS, FACTCHECK } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Counter({ value, decimals = 0, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30px" });
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.9,
      ease: EASE,
      onUpdate: (v) =>
        setDisplay(
          v.toLocaleString("es-MX", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
        ),
    });
    return () => controls.stop();
  }, [inView, value, decimals]);
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

function HeroPhoto({ config }) {
  const src = config?.hero_image_url || "/presidenta.webp";
  return (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
      className="relative flex justify-center lg:justify-end items-start h-full"
    >
      <div
        className="absolute bottom-4 left-1/2 lg:left-auto lg:right-6 -translate-x-1/2 lg:translate-x-0 w-[78%] max-w-[420px] aspect-square rounded-full bg-arena/30 blur-3xl"
        aria-hidden="true"
      />
      <div className="relative">
        <img
          src={src}
          alt="Lic. Mónica Zacil Villarreal Anaya, Presidenta Municipal de Tampico"
          data-testid="hero-presidenta-foto"
          className="relative w-full max-w-[360px] sm:max-w-[430px] lg:max-w-[500px] object-contain"
          style={{
            WebkitMaskImage: "linear-gradient(to bottom, black 58%, rgba(0,0,0,0.85) 74%, transparent 97%)",
            maskImage: "linear-gradient(to bottom, black 58%, rgba(0,0,0,0.85) 74%, transparent 97%)",
          }}
        />
        <div
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[64%] h-10 rounded-[100%] bg-guinda-dark/50 blur-xl"
          aria-hidden="true"
        />
        <div className="absolute -bottom-12 inset-x-0 flex justify-center pointer-events-none">
          <motion.p
            data-testid="hero-firma"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.35, ease: EASE }}
            className="text-center text-white text-[1.3rem] sm:text-3xl lg:text-4xl sm:whitespace-nowrap [text-shadow:0_3px_16px_rgba(45,20,26,0.65)]"
            style={{ fontFamily: "Yellowtail, cursive" }}
          >
            — Lic. Mónica Zacil Villarreal Anaya
          </motion.p>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
        className="hidden sm:block absolute -left-2 lg:left-0 bottom-28 bg-white rounded-xl shadow-lg border border-border px-4 py-3"
      >
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-guinda">Periodo</p>
        <p className="font-serif text-lg text-gris-charcoal">2024 — 2027</p>
      </motion.div>
    </motion.div>
  );
}

export default function Hero({ config, onSelectFact }) {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden grain"
      style={{ background: "linear-gradient(118deg, #8C2236 0%, #C03C54 46%, #BEB195 108%)" }}
    >
      <img
        src={config?.hero_panorama_url || "/hero-panorama.webp"}
        alt="Panorama del malecón de Tampico al atardecer"
        data-testid="hero-panorama-bg"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(74,16,28,0.92) 0%, rgba(140,34,54,0.78) 38%, rgba(140,34,54,0.35) 66%, rgba(190,177,149,0.18) 100%)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-guinda-dark/60 to-transparent pointer-events-none"
        aria-hidden="true"
      />
      {config?.hero_texture_url && (
        <img
          src={config.hero_texture_url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.08] mix-blend-overlay pointer-events-none"
        />
      )}
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          <div className="lg:col-span-7 flex flex-col items-center text-center">
            <motion.img
              src="/logo-hero.webp"
              alt="Segundo Informe de Gobierno — Lic. Mónica Zacil Villarreal Anaya, Presidenta Municipal de Tampico"
              data-testid="hero-logo"
              initial={{ opacity: 0, y: -24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="h-80 sm:h-96 lg:h-[30rem] w-auto object-contain mb-8"
            />
            <h1 className="sr-only">
              Segundo Informe de Gobierno — Lic. Mónica Zacil Villarreal Anaya, Presidenta Municipal de Tampico
            </h1>
            <MaskedLine delay={0.55} className="mt-2">
              <p
                data-testid="hero-frase"
                className="font-quote italic text-white text-xl sm:text-2xl lg:text-[1.75rem] leading-relaxed max-w-2xl [text-shadow:0_2px_20px_rgba(45,20,26,0.55)]"
              >
                “Un Tampico en donde las familias tienen condiciones de bienestar, cuentan con servicios y
                espacios públicos de calidad, se sienten seguras, acceden a una movilidad sostenible y disfrutan
                de una gran agenda cultural y deportiva que promueve una prosperidad compartida.”
              </p>
            </MaskedLine>

          </div>

          <div className="lg:col-span-5 lg:pl-6">
            <HeroPhoto config={config} />
          </div>
        </div>

        <div className="mt-16 md:mt-24 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {HERO_LOGROS.map((l, i) => (
            <motion.button
              key={l.id}
              type="button"
              data-testid={`hero-logro-card-${i}`}
              onClick={() => {
                const fact = FACTCHECK.find((f) => f.id === l.factId);
                if (fact) onSelectFact(fact);
              }}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
              whileHover={{ y: -6 }}
              className="group text-left bg-white rounded-2xl border border-border p-6 shadow-[0_14px_34px_-18px_rgba(90,82,74,0.4)] hover:border-guinda/50 transition-colors duration-300 cursor-pointer"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-verde-soft text-verde text-[11px] font-semibold px-2.5 py-1">
                <BadgeCheck className="w-3.5 h-3.5" aria-hidden="true" />
                Dato Verificado
              </span>
              <span className="block mt-5 font-serif text-3xl sm:text-4xl text-guinda tracking-tight">
                <Counter value={l.valor} decimals={l.decimals} prefix={l.prefix || ""} suffix={l.suffix || ""} />
              </span>
              <span className="block mt-2 text-sm text-gris leading-snug">{l.titulo}</span>
              <span className="mt-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-gris/70 group-hover:text-guinda transition-colors duration-200">
                {l.eje} · Ver soporte
                <ArrowDownRight className="w-3.5 h-3.5" aria-hidden="true" />
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
